import React, { useEffect, useRef, useState } from "react";
import { Megaphone, Plus, Trash2, Upload, Clock, Eye, EyeOff, Pencil, X, Link2 } from "lucide-react";
import { toast } from "sonner";
import client, { apiErr } from "@/lib/api";

const BE = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (u) => (!u ? "" : u.startsWith("http") ? u : `${BE}${u}`);
const EMPTY = { image: "", title: "", subtitle: "", cta_text: "", cta_link: "", duration: 5, active: true };

export default function AdminBanners() {
  const [banners, setBanners] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = () => client.get("/admin/banners").then((r) => setBanners(r.data)).catch(() => setBanners([]));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const reset = () => { setForm(EMPTY); setEditId(null); if (fileRef.current) fileRef.current.value = ""; };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const r = await client.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("image", r.data.url);
      toast.success("تم رفع الصورة");
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.image) { toast.error("ارفع صورة الإعلان أولاً"); return; }
    const payload = { ...form, duration: Math.max(1, Number(form.duration) || 5) };
    setSaving(true);
    try {
      if (editId) { await client.put(`/admin/banners/${editId}`, payload); toast.success("تم تحديث الإعلان"); }
      else { await client.post("/admin/banners", payload); toast.success("تمت إضافة الإعلان"); }
      reset(); load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const startEdit = (b) => {
    setEditId(b.id);
    setForm({ image: b.image, title: b.title, subtitle: b.subtitle, cta_text: b.cta_text, cta_link: b.cta_link, duration: b.duration, active: b.active });
    document.getElementById("sec-banners")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const toggle = async (b) => {
    try { await client.put(`/admin/banners/${b.id}`, { ...b, active: !b.active }); load(); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("حذف هذا الإعلان نهائياً؟")) return;
    try { await client.delete(`/admin/banners/${id}`); toast.success("تم الحذف"); if (editId === id) reset(); load(); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const count = banners?.length || 0;
  const activeCount = banners?.filter((b) => b.active).length || 0;

  return (
    <>
      <h2 id="sec-banners" className="font-head text-[15px] font-bold px-4 pt-5 pb-2 flex items-center gap-1.5">
        <Megaphone size={16} style={{ color: "var(--k-gold-deep)" }} /> إعلانات الواجهة ({count} · {activeCount} مُفعّل)
      </h2>

      {/* Editor */}
      <div className="rounded-2xl mx-4 p-4 mb-3" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
        <div className="flex items-center justify-between mb-2">
          <p className="font-head text-[13px] font-bold">{editId ? "تعديل إعلان" : "إضافة إعلان جديد"}</p>
          {editId && <button data-testid="banner-cancel-edit" onClick={reset} className="k-tap text-[11.5px] flex items-center gap-1" style={{ color: "var(--k-ruby)" }}><X size={13} /> إلغاء</button>}
        </div>

        {/* image upload */}
        <div className="mb-2">
          {form.image ? (
            <div className="relative rounded-xl overflow-hidden mb-2" style={{ aspectRatio: "16/7" }}>
              <img src={imgSrc(form.image)} alt="preview" className="w-full h-full object-cover" />
              <button data-testid="banner-remove-image" onClick={() => set("image", "")} className="k-tap absolute top-2 left-2 w-7 h-7 rounded-full flex items-center justify-center" style={{ background: "rgba(0,0,0,.55)", color: "#fff" }}><X size={14} /></button>
            </div>
          ) : null}
          <input ref={fileRef} data-testid="banner-file-input" type="file" accept="image/*" onChange={onFile} className="hidden" />
          <button data-testid="banner-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading} className="k-tap w-full py-2.5 rounded-lg text-[13px] font-bold flex items-center justify-center gap-1.5" style={{ background: "var(--k-paper)", border: "1px dashed var(--k-line)", color: "var(--k-ink)" }}>
            <Upload size={15} /> {uploading ? "جارٍ الرفع…" : form.image ? "تغيير الصورة" : "رفع صورة الإعلان"}
          </button>
        </div>

        <input data-testid="banner-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="العنوان (اختياري)" className="w-full rounded-lg px-3 py-2.5 text-[13px] mb-2" style={{ background: "var(--k-paper)", border: "1px solid var(--k-line)" }} />
        <input data-testid="banner-subtitle" value={form.subtitle} onChange={(e) => set("subtitle", e.target.value)} placeholder="الوصف (اختياري)" className="w-full rounded-lg px-3 py-2.5 text-[13px] mb-2" style={{ background: "var(--k-paper)", border: "1px solid var(--k-line)" }} />
        <div className="flex gap-2 mb-2">
          <input data-testid="banner-cta-text" value={form.cta_text} onChange={(e) => set("cta_text", e.target.value)} placeholder="نص الزر (مثال: ابدأ الآن)" className="flex-1 rounded-lg px-3 py-2.5 text-[13px]" style={{ background: "var(--k-paper)", border: "1px solid var(--k-line)" }} />
          <div className="flex-1 flex items-center rounded-lg px-2.5" style={{ background: "var(--k-paper)", border: "1px solid var(--k-line)" }}>
            <Link2 size={14} style={{ color: "var(--k-ink-soft)" }} />
            <input data-testid="banner-cta-link" value={form.cta_link} onChange={(e) => set("cta_link", e.target.value)} placeholder="رابط الزر (/browse أو https://)" className="w-full bg-transparent py-2.5 text-[13px] outline-none px-1.5" style={{ direction: "ltr", textAlign: "right" }} />
          </div>
        </div>
        <div className="flex gap-2 items-stretch">
          <div className="flex-1 flex items-center rounded-lg px-3" style={{ background: "var(--k-paper)", border: "1px solid var(--k-line)" }}>
            <Clock size={14} style={{ color: "var(--k-gold-deep)" }} />
            <input data-testid="banner-duration" type="number" min="1" value={form.duration} onChange={(e) => set("duration", e.target.value)} className="w-full bg-transparent py-2.5 text-[14px] font-num font-bold outline-none px-2" />
            <span className="text-[12px] whitespace-nowrap" style={{ color: "var(--k-ink-soft)" }}>ثانية عرض</span>
          </div>
          <button data-testid="banner-active-toggle" onClick={() => set("active", !form.active)} className="k-tap px-3 rounded-lg text-[12px] font-bold flex items-center gap-1.5" style={{ background: form.active ? "var(--k-emerald-tint)" : "var(--k-paper)", color: form.active ? "var(--k-emerald)" : "var(--k-ink-soft)", border: "1px solid var(--k-line)" }}>
            {form.active ? <Eye size={14} /> : <EyeOff size={14} />} {form.active ? "مُفعّل" : "متوقف"}
          </button>
        </div>

        <button data-testid="banner-save" onClick={save} disabled={saving} className="k-tap w-full mt-3 py-2.5 rounded-lg text-white font-bold text-[13px] flex items-center justify-center gap-1.5" style={{ background: "var(--k-ink)" }}>
          {editId ? <><Pencil size={15} /> حفظ التعديلات</> : <><Plus size={15} /> إضافة الإعلان</>}
        </button>
      </div>

      {/* List */}
      {banners === null ? (
        <div className="px-4 pb-2 text-center text-[13px]" style={{ color: "var(--k-ink-soft)" }}>جارٍ التحميل…</div>
      ) : banners.length === 0 ? (
        <div className="mx-4 mb-2 rounded-2xl p-6 text-center text-[13px]" style={{ background: "var(--k-surface)", border: "1px dashed var(--k-line)", color: "var(--k-ink-soft)" }}>
          لا توجد إعلانات بعد — أضف أول إعلان ليظهر في واجهة المتجر.
        </div>
      ) : (
        <div className="px-4 grid grid-cols-1 md:grid-cols-2 gap-2.5 pb-2">
          {banners.map((b) => (
            <div key={b.id} data-testid={`banner-card-${b.id}`} className="rounded-2xl overflow-hidden" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)", opacity: b.active ? 1 : 0.6 }}>
              <div className="relative" style={{ aspectRatio: "16/7" }}>
                <img src={imgSrc(b.image)} alt={b.title || "إعلان"} className="w-full h-full object-cover" />
                <span className="absolute top-2 right-2 text-[10.5px] font-bold px-2 py-1 rounded-full flex items-center gap-1 font-num" style={{ background: "rgba(0,0,0,.6)", color: "#fff" }}><Clock size={11} /> {b.duration}s</span>
                <span className="absolute top-2 left-2 text-[10.5px] font-bold px-2 py-1 rounded-full" style={{ background: b.active ? "var(--k-emerald)" : "rgba(0,0,0,.6)", color: "#fff" }}>{b.active ? "مُفعّل" : "متوقف"}</span>
              </div>
              <div className="p-3">
                <p className="font-head text-[13px] font-bold truncate">{b.title || "— بدون عنوان —"}</p>
                {b.subtitle && <p className="text-[11.5px] mt-0.5 line-clamp-2" style={{ color: "var(--k-ink-soft)" }}>{b.subtitle}</p>}
                <div className="flex gap-1.5 mt-2.5">
                  <button data-testid={`banner-toggle-${b.id}`} onClick={() => toggle(b)} className="k-tap flex-1 py-2 rounded-lg text-[11.5px] font-bold flex items-center justify-center gap-1" style={{ background: "var(--k-paper)", color: b.active ? "var(--k-gold-deep)" : "var(--k-emerald)", border: "1px solid var(--k-line)" }}>
                    {b.active ? <><EyeOff size={13} /> إيقاف</> : <><Eye size={13} /> تفعيل</>}
                  </button>
                  <button data-testid={`banner-edit-${b.id}`} onClick={() => startEdit(b)} className="k-tap flex-1 py-2 rounded-lg text-[11.5px] font-bold flex items-center justify-center gap-1" style={{ background: "var(--k-gold-tint)", color: "var(--k-gold-deep)", border: "1px solid var(--k-line)" }}><Pencil size={13} /> تعديل</button>
                  <button data-testid={`banner-del-${b.id}`} onClick={() => del(b.id)} className="k-tap px-3 py-2 rounded-lg" style={{ background: "var(--k-ruby-tint)", color: "var(--k-ruby)" }}><Trash2 size={13} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
